/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import Popup from "../../components/popups/popup";
import CustomInput from "../../components/inputs/customInput";
import AddIcon from '@mui/icons-material/Add';
import PostService from "../../services/postService";
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import PlaylistRemoveOutlinedIcon from '@mui/icons-material/PlaylistRemoveOutlined';
import { formatPrice } from "../../utils";
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import PagePaymentService from "../../services/pagePaymentService";
import PageDropdown from "../../components/dropdowns/pageDropdown";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import usePdfPagePayment from "../../components/prints/pdfs/pagePayment/pdfOnePagePaymentForAdd";

const AddPagePayment = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.insideInput, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [posts, setPosts] = useState([]);
    const [selectedPosts, setSelectedPosts] = useState([]);
    const [selectedPage, setSelectedPage] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });
    const { isLoading: isPDF, pdfPayment } = usePdfPagePayment();
    const pdfIntentRef = useRef(false);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);

    const [formValues, setFormValues] = useState({
        pageId: null,
        branchId: null,
        note: ''
    });

    const [addErrors, setAddErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isListLoading, setIsListLoading] = useState(false);
    const tableContainerRef = useRef(null);

    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newAddErrors = {};

        if (!formValues.pageId) {
            newAddErrors.page = t('pageRequired');
        }

        if (!formValues.branchId) {
            newAddErrors.branch = t('branchRequired');
        }

        if (selectedPosts.length === 0) {
            newAddErrors.general = t('selectAtLeastPost');
        }

        setAddErrors(newAddErrors);

        return Object.keys(newAddErrors).length === 0;
    };

    const handleSubmit = async (pdf = false) => {
        if (pdf) pdfIntentRef.current = true;

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);

                const data = await PagePaymentService.add({ ...formValues, postIds: selectedPosts });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);

                    if (pdfIntentRef.current) {
                        await pdfPayment(data.data.id);
                    }

                    setFormValues({
                        note: ''
                    });
                    setSelectedPage(null);
                    setSelectedBranch(null);
                    setSelectedPosts([]);
                    setPosts([]);
                }

                setAddErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setAddErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setAddErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
                pdfIntentRef.current = false;
            }
        }
    };

    const fetchPosts = async () => {
        if (isListLoading) return;
        setIsListLoading(true);
        try {
            const filters = {
                pageId: selectedPage?.id,
                ...sortState
            };

            const data = await PostService.fetchForPagePayment(filters);
            setPosts(data);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setIsListLoading(false);
        }
    };

    const calculatedTotalPrice = useMemo(() => {
        return posts
            .filter((post) => selectedPosts.includes(post.id))
            .reduce((acc, post) => acc + post.totalPrice, 0);
    }, [selectedPosts, posts]);

    const calculatedTransportationPrice = useMemo(() => {
        return posts
            .filter((post) => selectedPosts.includes(post.id))
            .reduce((acc, post) => acc + post.transportationPrice, 0);
    }, [selectedPosts, posts]);

    const remainingTotalPrice = useMemo(() => {
        return posts
            .filter((post) => !selectedPosts.includes(post.id))
            .reduce((acc, post) => acc + post.totalPrice, 0);
    }, [selectedPosts, posts]);

    const remainingTransportationPrice = useMemo(() => {
        return posts
            .filter((post) => !selectedPosts.includes(post.id))
            .reduce((acc, post) => acc + post.transportationPrice, 0);
    }, [selectedPosts, posts]);

    useEffect(() => {
        if (selectedPage) fetchPosts();
    }, [selectedPage, sortState]);

    return (
        <Box sx={{
            px: isTablet ? 0.6 : 2,
            py: 1,
            backgroundColor: theme.palette.secondary.main,
        }}>

            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    px: isTablet ? 1 : 4,
                    pb: 1
                }}
            >

                {/* row 1 */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: isTablet ? 'column' : 'row',
                    justifyContent: 'space-between'
                }}>
                    {/* page */}
                    <Box width={isTablet ? '100%' : '22%'}>
                        <PageDropdown
                            helperText={addErrors.page}
                            error={!!addErrors.page}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedPage={selectedPage}
                            setSelectedPage={setSelectedPage}
                            includeRemainAmount={true}
                        />
                    </Box>

                    {/* branch */}
                    <Box width={isTablet ? '100%' : '22%'}>
                        <BranchDropdown
                            helperText={addErrors.branch}
                            error={!!addErrors.branch}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedBranch={selectedBranch}
                            setSelectedBranch={setSelectedBranch}
                            includeRemainAmount={true}
                        />
                    </Box>

                    {/* note */}
                    <Box width={isTablet ? '100%' : '54%'}>
                        <CustomInput
                            label={t('note')}
                            placeholder={t('noteHere')}
                            name="note"
                            type='text'
                            value={formValues.note}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>

                {addErrors.general && (
                    <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                        {addErrors.general}
                    </Typography>
                )}

                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    sx={{
                        boxShadow: 'none',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        borderRadius: 2,
                        height: `calc(100vh - ${Object.keys(addErrors).length === 0 ? '390px' : '440px'})`,
                        overflowY: 'auto',
                        mt: 2
                    }}
                >
                    <Table stickyHeader aria-label="simple table">
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <TableRow>
                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}>
                                    {t('number')}
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('id')}
                                        {sortState.sortBy === 'id' && sortIcon()}
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('driver')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('driver')}
                                        {sortState.sortBy === 'driver' && sortIcon()}
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('invoiceNumber')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('invoiceNumber')}
                                        {sortState.sortBy === 'invoiceNumber' && sortIcon()}
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('totalPrice')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('totalPrice')}
                                        {sortState.sortBy === 'totalPrice' && sortIcon()}
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('transportationPrice')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('transportationPrice')}
                                        {sortState.sortBy === 'transportationPrice' && sortIcon()}
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('netPrice')}
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('address')}
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('createdAt')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('createdAt')}
                                        {sortState.sortBy === 'createdAt' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: selectedPosts.length === posts.length ? theme.palette.text.red : theme.palette.primary.main,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            if (selectedPosts.length === posts.length) {
                                                setSelectedPosts([]);
                                            } else {
                                                setSelectedPosts(posts.map((post) => post.id));
                                            }
                                        }}
                                    >
                                        {selectedPosts.length === posts.length ?
                                            <PlaylistRemoveOutlinedIcon />
                                            : <PlaylistAddCheckOutlinedIcon />
                                        }
                                        <Box width={3}></Box>
                                        <Typography variant="subtitle2">
                                            {selectedPosts.length === posts.length ? t('deselectAll') : t('selectAll')}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {posts.map((post, index) => {
                                const isSelected = selectedPosts.includes(post.id);

                                return (
                                    <TableRow
                                        key={post.id}
                                        onClick={() => {
                                            setSelectedPosts((prev) =>
                                                prev.includes(post.id)
                                                    ? prev.filter((id) => id !== post.id)
                                                    : [...prev, post.id]
                                            );
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: isSelected
                                                ? alpha(theme.palette.primary.main, 0.5)
                                                : 'inherit',
                                            transition: 'background-color 0.01s ease-in-out',
                                        }}
                                    >
                                        <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{index + 1}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}>{post.id}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}>{post.driver ? post.driver.name : "-"}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}>{post.invoiceNumber}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(post.totalPrice)}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(post.transportationPrice)}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(post.netPrice)}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}>{post.receiverAddress}, {post.street.name}, {post.street.city.name}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}>{post.createdAt}</TableCell>
                                        <TableCell align={isEn ? 'left' : 'right'}></TableCell>
                                    </TableRow>
                                )
                            })}

                        </TableBody>
                    </Table>
                </TableContainer>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        padding: '12px',
                        backgroundColor: theme.palette.customGrey.insideInput,
                        borderRadius: 1,
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        mt: 2
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box width={isTablet ? '100%' : '27%'}>
                            <Typography variant="subtitle2">
                                {t('totalPrice')}: <strong>{formatPrice(calculatedTotalPrice)}</strong>
                            </Typography>
                        </Box>
                        <Box width={isTablet ? '100%' : '30%'}>
                            <Typography variant="subtitle2">
                                {t('transportationPrice')}: <strong>{formatPrice(calculatedTransportationPrice)}</strong>
                            </Typography>
                        </Box>
                        <Box width={isTablet ? '100%' : '27%'}>
                            <Typography variant="subtitle2">
                                {t('netPrice')}: <strong>{formatPrice(calculatedTotalPrice - calculatedTransportationPrice)}</strong>
                            </Typography>
                        </Box>
                        <Box width={isTablet ? '100%' : '16%'}>
                            <Typography variant="subtitle2">
                                {t('totalPosts')}: <strong>{selectedPosts.length}</strong>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Remaining Amounts */}
                    <Box sx={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Box width={isTablet ? '100%' : '27%'}>
                            <Typography variant="subtitle2">
                                {t('remainingTotalPrice')}: <strong>{formatPrice(remainingTotalPrice)}</strong>
                            </Typography>
                        </Box>
                        <Box width={isTablet ? '100%' : '30%'} bgcolor={'red'}>
                            <Typography variant="subtitle2">
                                {t('remainingTransportationPrice')}: <strong>{formatPrice(remainingTransportationPrice)}</strong>
                            </Typography>
                        </Box>
                        <Box width={isTablet ? '100%' : '27%'} bgcolor={'red'}>
                            <Typography variant="subtitle2">
                                {t('remainingNetPrice')}: <strong>{formatPrice(remainingTotalPrice - remainingTransportationPrice)}</strong>
                            </Typography>
                        </Box>
                        <Box width={isTablet ? '100%' : '16%'}>
                            <Typography variant="subtitle2">
                                {t('totalRemainingPosts')}: <strong>{posts.length - selectedPosts.length}</strong>
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 1, width: isTablet ? '100%' : '30%' }}>
                        <Typography variant="subtitle2">
                            {t('remainingTotalPricePage')}: <strong>{formatPrice(selectedPage?.remainAmount || 0)}</strong>
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    mt: addErrors.general ? 1 : 2
                }}>
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', width: 140, height: 40 }}
                        onClick={() => handleSubmit(false)}
                    >
                        {(isLoading && !isPDF) ? <CircularProgress color="secondary" size={28} /> :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('add')}</p>
                                <AddIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        }
                    </Button>

                    <Box width={20}> </Box>

                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', width: 140, height: 40 }}
                        onClick={() => handleSubmit(true)}
                    >
                        {isLoading || isPDF ? <CircularProgress color="secondary" size={28} />
                            :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('pdf')}</p>
                                <Box width={'6px'}> </Box>
                                <PictureAsPdfOutlinedIcon fontSize="small" />
                            </Box>
                        }
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default AddPagePayment;